<template>
  <v-card>
    <v-card-title> IFA összesítők </v-card-title>

    <v-card-text>
      <!-- Filters -->
      <v-row class="mb-4" align="center" dense>
        <!-- Company selector -->
        <v-col cols="12" sm="3">
          <v-select
            v-model="selectedCompanyId"
            :items="companies"
            item-title="name"
            item-value="id"
            label="Cég"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <v-select
            v-model="selectedCostCenterId"
            :items="costCenters"
            item-title="name"
            item-value="id"
            label="Költséghely"
          />
        </v-col>

        <v-col cols="12" sm="2">
          <v-select v-model="year" :items="years" label="Év" />
        </v-col>

        <v-col cols="12" sm="2">
          <v-select v-model="month" :items="months" label="Hónap" />
        </v-col>

        <v-col cols="12" sm="3">
          <v-btn
            color="primary"
            @click="loadReport"
            :loading="reportLoading"
            :disabled="
              reportLoading || !selectedCompanyId || !selectedCostCenterId
            "
            >Összesítő betöltése</v-btn
          >
        </v-col>
        <v-col cols="12" sm="3">
          <v-btn
            color="primary"
            @click="createSheet"
            :loading="sheetLoading"
            :disabled="
              sheetLoading || !selectedCompanyId || !selectedCostCenterId
            "
            >Táblázat létrehozása</v-btn
          >
        </v-col>
      </v-row>

      <v-alert v-if="sheetUrl" type="success" variant="tonal" class="mb-4">
        A táblázat elkészült:
        <a :href="sheetUrl" target="_blank">{{ sheetUrl }}</a>
      </v-alert>

      <v-alert
        v-if="reportLoaded && rows.length === 0"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Nincs adat a kiválasztott időszakra.
      </v-alert>

      <!-- Table -->
      <v-table>
        <thead>
          <tr>
            <th>Város</th>
            <th class="totals-column">IFA egységár</th>
            <th class="totals-column">IFA darab összesen</th>
            <th class="totals-column">IFA összeg</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in rows" :key="(row.city ?? '') + '-' + row.ifaAmount">
            <td>{{ row.city || "Nincs város megadva" }}</td>
            <td class="totals-column">{{ row.ifaAmount }}</td>
            <td class="totals-column">{{ row.totalCount }}</td>
            <td class="totals-column">{{ row.totalIfa }}</td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td colspan="2"><strong>Végösszeg</strong></td>

            <td class="totals-column">
              <strong>{{ grandTotalCount }}</strong>
            </td>

            <td class="totals-column">
              <strong>{{ grandTotalIfa }}</strong>
            </td>
          </tr>
        </tfoot>
      </v-table>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import axiosInstance from "@/api/axiosInstance";
import { CompanyService } from "@/services/CompanyService";
import { CostCenterService } from "@/services/CostCenterService";

/* ---------------- STATE ---------------- */
const companyService = new CompanyService("/admin/companies");
const costCenterService = new CostCenterService("/admin/companies");
const rows = ref([]);
const sheetUrl = ref("");
const sheetLoading = ref(false);
const reportLoading = ref(false);
const reportLoaded = ref(false);

const selectedCompanyId = ref(null);
const companies = ref([]);
const selectedCostCenterId = ref(null);
const costCenters = ref([]);

const year = ref(new Date().getFullYear());
const month = ref(new Date().getMonth() + 1);

const years = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() - 5 + i
);
const months = Array.from({ length: 12 }, (_, i) => i + 1);

/* ---------------- COMPUTED ---------------- */

const formattedMonth = computed(() => {
  return `${year.value}-${String(month.value).padStart(2, "0")}`;
});

const grandTotalCount = computed(() =>
  rows.value.reduce((sum, row) => sum + (row.totalCount || 0), 0)
);

const grandTotalIfa = computed(() =>
  rows.value.reduce((sum, row) => sum + (row.totalIfa || 0), 0)
);

/* ---------------- METHODS ---------------- */

const fetchCompanies = async () => {
  try {
    companies.value = await companyService.getAll();
  } catch (error) {
    console.error("Failed to fetch companies:", error);
  }
};

const fetchCostCenters = async () => {
  try {
    costCenters.value = await costCenterService.adminGetAllForCompany(
      selectedCompanyId.value
    );
  } catch (error) {
    console.error("Failed to fetch cost centers", error);
  }
};

const loadReport = async () => {
  if (!selectedCompanyId.value || !selectedCostCenterId.value) return;

  reportLoading.value = true;
  try {
    const res = await axiosInstance.get(
      `/api/v1/admin/companies/${selectedCompanyId.value}/cost-centers/${selectedCostCenterId.value}/ifa-summaries/monthly`,
      {
        params: {
          month: formattedMonth.value,
        },
      }
    );

    rows.value = res.data;
    reportLoaded.value = true;
  } catch (error) {
    console.error("Failed to load IFA report:", error);
  } finally {
    reportLoading.value = false;
  }
};

const createSheet = async () => {
  if (!selectedCompanyId.value || !selectedCostCenterId.value) return;
  sheetLoading.value = true;
  sheetUrl.value = "";
  try {
    const res = await axiosInstance.get(
      `/api/v1/admin/companies/${selectedCompanyId.value}/cost-centers/${selectedCostCenterId.value}/ifa-summaries/monthly-sheet`,
      { params: { month: formattedMonth.value } }
    );
    sheetUrl.value = res.data;
  } catch (error) {
    console.error("Failed to export IFA report:", error);
  } finally {
    sheetLoading.value = false;
  }
};

watch(selectedCompanyId, async (newCompanyId) => {
  if (!newCompanyId) return;

  selectedCostCenterId.value = null;
  costCenters.value = [];

  await fetchCostCenters(newCompanyId);
});

/* ---------------- LIFECYCLE ---------------- */

onMounted(() => {
  fetchCompanies();
});
</script>

<style scoped>
.totals-column {
  font-weight: bold;
  text-align: center;
}
</style>
